/**
 * Guna Tattva D24 Calculation Logic
 * Based on Vaisheshika philosophy - 24 qualities (attributes) of all objects
 *
 * Structure:
 * - 24 Gunas: qualities present in five elements and mind
 * - Each Guna = 1°15' (1.25 degrees)
 * - Formula: Guna_number = floor(degrees × 24 ÷ 30) + 1
 */

import { JatakaResponse, GrahaPosition } from '../../../../../models/api';

export interface PlanetDisplay {
  name: string;
  short_name: string;
  longitude: number;
  is_retrograde: boolean;
  is_lagna?: boolean;
  is_hora?: boolean;
  is_chandra?: boolean;
}

/**
 * 24 Guna types
 */
export const GUNA_TYPES = [
  'RUPA', 'RASA', 'GANDHA', 'SPARSHA',
  'SANKHYA', 'PARIMANA',
  'PRITHAKTVA', 'SAMYOGA', 'VIBHAGA', 'PARATVA', 'APARATVA',
  'GURUTVA', 'DRAVATVA', 'SNEGHA',
  'SHABDA',
  'JNANA', 'SUKHA', 'DUKHA', 'ICHCHHA', 'DVESHA',
  'PRAYATNA',
  'DHARMA', 'ADHARMA', 'SAMSKARA'
] as const;

export type GunaType = typeof GUNA_TYPES[number];

/**
 * Guna descriptions with transliteration and English meaning
 */
export const GUNA_DESCRIPTIONS: Record<GunaType, {
  transliteration: string;
  english: string;
  meaning: string;
}> = {
  RUPA: {
    transliteration: 'Rūpa',
    english: 'Form',
    meaning: 'Form, color, visible'
  },
  RASA: {
    transliteration: 'Rasa',
    english: 'Taste',
    meaning: 'Taste, flavor'
  },
  GANDHA: {
    transliteration: 'Gandha',
    english: 'Smell',
    meaning: 'Smell, fragrance'
  },
  SPARSHA: {
    transliteration: 'Sparśa',
    english: 'Touch',
    meaning: 'Touch, tactile sensation'
  },
  SANKHYA: {
    transliteration: 'Saṅkhyā',
    english: 'Number',
    meaning: 'Numbers, calculations, mathematics'
  },
  PARIMANA: {
    transliteration: 'Parimāṇa',
    english: 'Measure',
    meaning: 'Measurement, volume, comparison'
  },
  PRITHAKTVA: {
    transliteration: 'Pṛthaktva',
    english: 'Separateness',
    meaning: 'Separateness, individuality'
  },
  SAMYOGA: {
    transliteration: 'Saṃyoga',
    english: 'Conjunction',
    meaning: 'Conjunction, union'
  },
  VIBHAGA: {
    transliteration: 'Vibhāga',
    english: 'Division',
    meaning: 'Division, distinction'
  },
  PARATVA: {
    transliteration: 'Paratva',
    english: 'Remoteness',
    meaning: 'Remoteness, distance'
  },
  APARATVA: {
    transliteration: 'Aparatva',
    english: 'Proximity',
    meaning: 'Proximity, nearness'
  },
  GURUTVA: {
    transliteration: 'Gurutva',
    english: 'Weight',
    meaning: 'Weight, heaviness, gravity'
  },
  DRAVATVA: {
    transliteration: 'Dravatva',
    english: 'Fluidity',
    meaning: 'Fluidity, flow'
  },
  SNEGHA: {
    transliteration: 'Snegha',
    english: 'Viscosity',
    meaning: 'Viscosity, oiliness'
  },
  SHABDA: {
    transliteration: 'Śabda',
    english: 'Sound',
    meaning: 'Sound, vibration'
  },
  JNANA: {
    transliteration: 'Jñāna',
    english: 'Knowledge',
    meaning: 'Knowledge, cognition'
  },
  SUKHA: {
    transliteration: 'Sukha',
    english: 'Happiness',
    meaning: 'Happiness, pleasure'
  },
  DUKHA: {
    transliteration: 'Duḥkha',
    english: 'Suffering',
    meaning: 'Suffering, pain'
  },
  ICHCHHA: {
    transliteration: 'Icchā',
    english: 'Desire',
    meaning: 'Desire, wish'
  },
  DVESHA: {
    transliteration: 'Dveṣa',
    english: 'Aversion',
    meaning: 'Aversion, dislike'
  },
  PRAYATNA: {
    transliteration: 'Prayatna',
    english: 'Effort',
    meaning: 'Effort, exertion, willpower'
  },
  DHARMA: {
    transliteration: 'Dharma',
    english: 'Virtue',
    meaning: 'Virtue, righteousness, duty'
  },
  ADHARMA: {
    transliteration: 'Adharma',
    english: 'Vice',
    meaning: 'Vice, unrighteousness'
  },
  SAMSKARA: {
    transliteration: 'Saṃskāra',
    english: 'Impressions',
    meaning: 'Impressions, karmic imprints, rituals'
  }
};

/**
 * Calculate Guna number from degrees in rashi (0-30)
 */
export function calculateGunaNumber(degreeInRashi: number): number {
  // Each Guna = 30° ÷ 24 = 1.25° (1°15')
  // Guna_number = floor(degrees × 24 ÷ 30) + 1
  const gunaIndex = Math.floor(degreeInRashi * 24 / 30);
  return Math.min(gunaIndex + 1, 24);
}

/**
 * Get Guna type from Guna number (1-24)
 */
export function getGunaType(gunaNumber: number): GunaType {
  return GUNA_TYPES[gunaNumber - 1] || GUNA_TYPES[0];
}

/**
 * Single planet's Guna Tattva attributes
 */
export interface GunaTattvaAttributes {
  planet: PlanetDisplay;
  gunaNumber: number;
  gunaType: GunaType;
}

/**
 * Complete Guna Tattva data for all planets
 */
export interface GunaTattvaData {
  divisions: GunaTattvaAttributes[];
}

/**
 * Parse longitude from position string format "rashi|degrees|longitude|speed"
 */
export function parseLongitudeFromPosition(position: string): number {
  if (!position) {
    return 0;
  }

  const parts = position.split('|');
  if (parts.length >= 3) {
    return parseFloat(parts[2]);
  }

  return 0;
}

/**
 * Convert API planet to PlanetDisplay format
 */
export function convertToPlanetDisplay(planet: GrahaPosition, currentHoraPlanet?: string): PlanetDisplay {
  const longitude = parseLongitudeFromPosition(planet.position || '');

  const isHora = currentHoraPlanet && (
    planet.short_name === currentHoraPlanet ||
    planet.name === currentHoraPlanet
  );

  return {
    name: planet.name,
    short_name: planet.short_name,
    longitude: longitude,
    is_retrograde: planet.is_retrograde || false,
    is_lagna: planet.short_name === 'Lg',
    is_hora: isHora || false,
    is_chandra: planet.short_name === 'Ch' || planet.name === 'Chandra'
  };
}

/**
 * Calculate Guna Tattva for all planets
 */
export function calculateGunaTattvas(apiResponse: JatakaResponse): GunaTattvaData {
  const divisions: GunaTattvaAttributes[] = [];

  if (!apiResponse.graha_positions?.planets) {
    return { divisions };
  }

  // Get current hora planet for highlighting
  const currentHoraPlanet = apiResponse.detailed_panchanga?.hora_details?.current_planet;

  apiResponse.graha_positions.planets.forEach(planet => {
    // Convert to PlanetDisplay format
    const planetDisplay = convertToPlanetDisplay(planet, currentHoraPlanet);

    // Calculate degree within current rashi (0-30)
    const degreeInRashi = planetDisplay.longitude % 30;

    // Calculate Guna number (1-24)
    const gunaNumber = calculateGunaNumber(degreeInRashi);

    // Get Guna type
    const gunaType = getGunaType(gunaNumber);

    divisions.push({
      planet: planetDisplay,
      gunaNumber,
      gunaType
    });
  });

  return { divisions };
}
